import { SHOW_ON_HOVER_CLASS } from "@components/common/data-grid/adm-data-grid-utils";
import { Link, Tooltip } from "@flat-ui";
import { usePermission } from "@hooks/use-permission";
import { AllMemberTypes } from "@custom-types/base-types";
import { useResendInvitationToWorkspace } from "@components/use-resend-invitation-to-workspace";
import { LocationProp } from "@utils/track-event/event-types";

interface Props extends LocationProp {
  /** The member to resend invitation to workspace */
  member: AllMemberTypes;
}

/**
 * Resend invite for a single unregistered member to workspace
 * This component is hidden by default and it is shown on table hover using ${SHOW_ON_HOVER_CLASS}.
 */
export function ResendInvitationToWorkspace({
  member,
  location,
}: Props): React.JSX.Element | null {
  const { onResendInvitationClick, shouldHideResendInvitation } =
    useResendInvitationToWorkspace({
      member,
      location,
    });

  const { isAllowed, deniedReason } = usePermission("member.resendInvitation");

  if (shouldHideResendInvitation) {
    return null;
  }

  return (
    <Tooltip title={deniedReason}>
      <Link
        // Always a handler, so that it renders as a button that keyboard users can reach to see why
        onClick={() => {
          if (isAllowed) {
            void onResendInvitationClick();
          }
        }}
        className={SHOW_ON_HOVER_CLASS}
        aria-disabled={!isAllowed}
        sx={{
          ml: "10px",
          // This will be overridden by parent component using SHOW_ON_HOVER_CLASS, which also
          // shows it when the row has keyboard focus, as a hidden element can't be focused
          visibility: "hidden",
        }}
      >
        Resend Invite
      </Link>
    </Tooltip>
  );
}
